import { Component, inject, ViewChild } from "@angular/core";
import { IonApp, IonRouterOutlet, Platform, IonSplitPane } from "@ionic/angular/standalone";
import { TranslationService } from "./shared/services/translation.service";
import { TranslateService } from "@ngx-translate/core";
import { NetworkService } from "./shared/services/network.service";
import { firstValueFrom } from "rxjs";
import { DataService } from "./shared/services/data.service";
import { HttpService } from "./shared/services/http.service";
import { StorageService, } from "./shared/services/storage.service";
import AppConstants from "./shared/constants/app.constants";
import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { v4 as uuidv4 } from "uuid";
import { Capacitor } from "@capacitor/core";
import { Filesystem } from "@capacitor/filesystem";
import { SidemenuPage } from "./pages/sidemenu/sidemenu.page";
import { AuthService } from "./shared/services/auth.service";
import { TimezoneService } from "./shared/services/timezone.service";
import { ThemeService } from "./shared/services/theme.service";
import { ChatService } from "./shared/services/chat.service";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  imports: [IonApp, IonRouterOutlet, IonSplitPane, SidemenuPage],
})
export class AppComponent {
  deviceInfo: any;
  token: any;
  private themeService = inject(ThemeService);

  @ViewChild(IonRouterOutlet) private routerOutlet?: IonRouterOutlet;

  constructor(
    private translationService: TranslationService,
    private translateService: TranslateService,
    private networkService: NetworkService,
    private dataService: DataService,
    private httpService: HttpService,
    private storageService: StorageService,
    private authService: AuthService,
    private timezoneService: TimezoneService,
    private chatService: ChatService,
  ) {
    this.networkService.status$.subscribe((online) => {
      if (online) {
        this.syncOfflineNotes();
        this.syncOfflineScreenshots();
        this.syncOfflineChatMessages();
      }
    });
  }

  // Runs globally (not tied to chat.page.ts being open) so pending chat messages/attachments sync
  // as soon as connectivity returns, whichever page the user is currently on. Requires an active login.
  private syncOfflineChatMessages(): void {
    if (!this.authService.authToken.value || !this.networkService.getStatus()) {
      return;
    }

    void this.chatService.syncQueuedMessages();
  }

  async checkWritePermission(): Promise<boolean> {
    if (Capacitor.getPlatform() !== "android") return true;

    const status = await Filesystem.requestPermissions();
    return status.publicStorage === "granted";
  }

  async ngOnInit() {
    await this.timezoneService.init();
    this.themeService.init();
    this.initResumeRefresh();
    this.ensureDeviceInfo();
    this.getAppVersion();
    this.networkService.initNetworkWatcher();
    const selectedLang = this.translationService.getSelectedLang();
    this.translationService
      .getTranslation(selectedLang)
      .subscribe((translations) => {
        this.translateService.setTranslation(selectedLang, translations, true);
        this.translateService.use(selectedLang);
      });

    this.getToken();

    this.authService.authToken.subscribe((token) => {
      this.token = token;
      if (token) {
        this.syncOfflineChatMessages();
      } else {
        this.chatService.clearInMemoryTokens();
      }
    });
  }

  /**
   * Ionic keeps pages alive in the navigation stack, so ionViewDidEnter is not
   * fired again when the app returns from the background. Re-invoke it manually
   * on the currently visible page so every page refreshes its data on resume.
   */
  private async initResumeRefresh() {
    await App.addListener("resume", () => {

      const page = this.getActivePage();
      if (typeof page?.ionViewDidEnter === "function") {
        try {
          page.ionViewDidEnter();
        } catch (err) {
          console.error("Resume refresh failed", err);
        }
      }
    });
  }

  /** Returns the component instance of the page currently shown in the outlet. */
  private getActivePage(): { ionViewDidEnter?: () => unknown } | null {
    let outlet: IonRouterOutlet | undefined = this.routerOutlet;
    let page: any = null;

    // Walk nested outlets (e.g. detail pages with their own outlet) down to the visible page
    while (outlet?.isActivated) {
      page = outlet.component;
      outlet = page?.routerOutlet instanceof IonRouterOutlet ? page.routerOutlet : undefined;
    }

    return page;
  }

  async syncOfflineNotes() {
    const offlineNotes = await this.dataService.getAllNotes();

    for (const note of offlineNotes) {
      if (!note.synced) {
        const payload = {
          alarm_id: note.alarm_id,
          note: note.note,
          title: note.title,
          lang: note.lang,
        };
        try {
          const res = await this.httpService.post("user/alarm/getSaveAlarmNotes", payload);

          if (res?.data?.lastInsertedID && note.image) {
            const blob = this.base64ToBlob(note.image);
            const formData = new FormData();
            formData.append("file", blob, "profile.jpg");
            formData.append("lastinsertedID", res.data.lastInsertedID);
            await this.httpService.post("user/alarm/alarmNoteImages", formData);
          }

          await this.dataService.deleteNote(note._id, note._rev);
        } catch (err) {
          console.error("Sync failed for note", note, err);
        }
      }
    }
  }

  base64ToBlob(base64: string): Blob {
    const byteString = atob(base64.split(",")[1]);
    const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  async syncOfflineScreenshots() {
    const offlineShots = await this.dataService.getAllScreenShots();

    for (const shot of offlineShots) {
      if (!shot.synced) {
        try {
          const payload = {
            file_64: shot.file_64,
            lang: this.storageService.getItem(AppConstants.SELECTED_LANGUAGE),
            fileName: shot.fileName,
            alarmId: Number(shot.alarmId),
          };

          const res = await this.httpService.post("user/other/getUploadScreenShotPDF", payload);

          if (res?.data?.pdf_name) {
            const monitorPayload = {
              usertext: shot.fileName,
              alarm_id: String(shot.alarmId),
              image: res.data.pdf_name,
              lang: this.storageService.getItem(AppConstants.SELECTED_LANGUAGE),
            };

            await this.httpService.post("user/entity/saveMonitorImage",monitorPayload);

            await this.dataService.deleteScreenShot(shot._id, shot._rev);
          }
        } catch (err) {
          console.error("Sync failed for screenshot", shot, err);
        }
      }
    }
  }

  private async ensureDeviceInfo() {
    try {
      const info = await Device.getInfo();
      const id = await Device.getId();

      let deviceUUID = id?.identifier;

      if (!deviceUUID) {
        const storedInfo = await this.storageService.getItemIonic(
          AppConstants.DEVICE_INFO,
        );
        if (storedInfo?.uuid) {
          deviceUUID = storedInfo.uuid;
        } else {
          deviceUUID = uuidv4();
        }
      }

      this.deviceInfo = {
        ...info,
        uuid: deviceUUID,
      };

      await this.storageService.setItemIonic(
        AppConstants.DEVICE_INFO,
        this.deviceInfo,
      );
    } catch (error) {
      console.error("Error getting device info:", error);
    }
  }

  async getAppVersion() {
    try {
      const appInfo = Capacitor.isNativePlatform() ? await App.getInfo() : null;
      const version =
        appInfo?.version ||
        (await this.storageService.getItemIonic(AppConstants.APP_VERSION)) ||
        "web";

      await this.storageService.setItemIonic(AppConstants.APP_VERSION, version);
    } catch (error) {
      console.error("Error getting app version:", error);
      await this.storageService.setItemIonic(AppConstants.APP_VERSION, "web");
    }
  }

  async getToken() {
    this.token = await this.storageService.getItemIonic(AppConstants.AUTH_TOKEN);
  }
}
